// ============================================================================
// PendingApproval — full-shell screen shown when the authenticated user's
// user_profiles.status is 'pending'. The JWT bridge still mints a token
// (so reads of public reference data work for diagnostic purposes), but
// the client routes the user here until an admin flips them to 'active'
// via /admin/users (provision_user RPC).
//
// Mounted inline by NavShell after the "no employee record" gate so it
// replaces the authenticated shell entirely — no nav, no business modules.
// ============================================================================

import { Hourglass, LogOut } from 'lucide-react';
import { Button } from './components';

interface Props {
  email: string | undefined;
  displayName: string | null | undefined;
  onSignOut: () => void;
}

export function PendingApproval({ email, displayName, onSignOut }: Props) {
  return (
    <div className="flex h-full items-center justify-center bg-tcs-bg-muted p-6">
      <div className="w-full max-w-md rounded-xl border border-st-warn-border-strong bg-st-warn-bg/60 p-6 text-sm text-st-warn-fg-strong">
        <div className="flex items-start gap-3">
          <Hourglass className="mt-0.5 h-5 w-5 shrink-0 text-st-warn-fg" />
          <div className="space-y-2">
            <p className="font-semibold">Your account is pending approval.</p>
            <p>
              {displayName ? <strong>{displayName}</strong> : 'You'}
              {' '}
              ({email ? <span className="font-mono text-xs">{email}</span> : 'this account'})
              {' '}
              has been signed in but isn't yet activated for TCS App access.
              An admin needs to assign you a role before you can use the
              business modules.
            </p>
            <p>
              Contact your manager or{' '}
              <a
                href="mailto:ops@terminalcleaning.com"
                className="underline underline-offset-2"
              >
                ops@terminalcleaning.com
              </a>{' '}
              if you've been waiting.
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={onSignOut}
              icon={<LogOut className="h-4 w-4" />}
              className="mt-2"
            >
              Sign out
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
